import {callCreditClient, getCreditFulfillment, getCreditPullInfo} from '../actions/credit';
import {getLangCode, interpolate} from '../stringBundle';

import utils from './utils';

const EFX_PARTNER_CODE = 'EFX';
const TU_PARTNER_CODE = 'TU';
const EXP_PARTNER_CODE = 'EXP';

/**
 * Returns the report and refresh types based on the provided parameters.
 *
 * @param {boolean} isPull1BReportAllowed - Flag indicating if pulling a 1B report is allowed.
 * @param {string} topTabId - The ID of the top tab.
 * @param {string} primaryBureau - The primary bureau to use for the report.
 * @returns {{refreshType: RefreshType; reportType: ReportType}} An object containing the report and refresh types.
 */
const getReportAndRefreshType = (
  isPull1BReportAllowed: boolean,
  topTabId: string,
  primaryBureau = 'EFX'
): {refreshType: RefreshType; reportType: ReportType} => {
  if (primaryBureau === 'TU') {
    return {refreshType: 'CREDIT_REPORT_AND_SCORE', reportType: 'CA_1B'};
  }
  if (topTabId === TU_PARTNER_CODE || topTabId === EXP_PARTNER_CODE) {
    return {refreshType: 'CREDIT_REPORT_AND_SCORE', reportType: 'US_3B'};
  }
  return isPull1BReportAllowed
    ? {refreshType: 'CREDIT_REPORT_AND_SCORE', reportType: 'US_EFX'}
    : {refreshType: 'CREDIT_SCORE', reportType: 'US_EFX'};
};

/**
 * Handles the success scenario for credit fulfillment.
 * Dispatches actions to get credit pull information and call EFX.
 *
 * @param {Function} dispatch - Redux dispatch function.
 * @param {string} accountId - Account ID of the user.
 * @param {StringMap} localizedStringBundle - Localized string bundle for the current language.
 * @param {boolean} is3BUser - Flag indicating if the user is a 3B user.
 * @param {JWT} jwt - JSON Web Token for authentication.
 * @param {string} bureau - The bureau to use for the credit pull (default is 'EFX')(EFX|TU).
 *
 */
const handleCreditFulfillmentSuccess = async(
  dispatch: Function,
  accountId: string,
  localizedStringBundle: StringMap,
  is3BUser: boolean,
  bureau: string,
  countryCode: string,
  creditPullInfo?: CreditPullInfo,
  isUserInitiatedPull = false
) => {
  try {
    // const pullInfoRes = await dispatch(getCreditPullInfo(accountId));
    // const creditPullInfo = utils.safeGet(pullInfoRes, 'pullInfo', {});
    await dispatch(getCreditPullInfo(accountId));
    await dispatch(
      callCreditClient(
        accountId,
        localizedStringBundle,
        getLangCode(),
        is3BUser,
        bureau,
        countryCode,
        creditPullInfo,
        isUserInitiatedPull
      )
    );
  } catch (pullInfoError) {
    dispatch({type: 'CREDIT_SCORE_STATUS', credit_score_status: 'error'});
    dispatch({type: 'CREDIT_REPORT_STATUS', credit_report_status: 'error'});
  }
};

/**
 * Processes the Equifax credit pull by dispatching various actions.
 * Handles the success and failure scenarios for credit fulfillment.
 *
 * @param {Object} creditPullInfo - Information about the credit pull.
 * @param {string} topTabId - The ID of the top tab.
 * @param {boolean} is3BUser - Flag indicating if the user is a 3B user.
 * @param {Object} props - Properties containing dispatch function and authentication information.
 * @param {Function} props.dispatch - Redux dispatch function.
 * @param {Object} props.auth - Authentication information.
 * @param {Object} props.auth.user - User information.
 * @param {Object} props.auth.user.primaryMember - Primary member information.
 * @param {string} props.auth.user.primaryMember.accountId - Account ID of the user.
 * @param {StringMap} localizedStringBundle - Localized string bundle for the current language.
 * @param {string} bureau - The bureau to use for the credit pull (default is 'EFX')(EFX|TU).
 * @param {string} countryCode - Country code (e.g. 'US', 'CA').
 * @param {boolean} [isUserInitiatedPull=false] - True when the user explicitly clicked to pull (e.g. "Get Latest Score & Report"); used for analytics.
 */
export const processCreditPull = async(
  creditPullInfo: CreditPullInfo,
  topTabId: string,
  is3BUser: boolean,
  props: $TSFixMe,
  localizedStringBundle: StringMap,
  bureau: string,
  countryCode: string,
  isUserInitiatedPull = false
) => {
  const {isPull1BReportAllowed} = creditPullInfo;
  const {refreshType, reportType} = getReportAndRefreshType(isPull1BReportAllowed, topTabId, bureau);
  const accountId: string = utils.safeGet(props.auth, 'user.primaryMember.accountId', '');

  try {
    const fulfillmentResponse = await props.dispatch(getCreditFulfillment(refreshType, reportType, accountId));

    if (fulfillmentResponse.type === 'CREDIT_FULFILLMENT_SUCCESS') {
      props.dispatch({type: 'CREDIT_SCORE_STATUS', credit_score_status: 'loading'});
      props.dispatch({type: 'CREDIT_REPORT_STATUS', credit_report_status: 'loading'});
      await handleCreditFulfillmentSuccess(
        props.dispatch,
        accountId,
        localizedStringBundle,
        is3BUser,
        bureau,
        countryCode,
        creditPullInfo,
        isUserInitiatedPull
      );
    } else {
      props.dispatch({
        type: 'CREDIT_FULFILLMENT_FAILURE',
        creditFulfillmentStatus: 'error',
        error: fulfillmentResponse.error,
      });
    }
  } catch (error) {
    props.dispatch({
      type: 'CREDIT_FULFILLMENT_FAILURE',
      creditFulfillmentStatus: 'error',
      error: error,
    });
  }
};

export type LogDataObj = {
  feature: string;
  actionType?: string;
  message?: string;
  startTime: number;
  accountId?: string;
  clientId: string;
  statusCode?: number;
  error?: object | string;
};

/**
 * Returns Object of gaAction and text for Ga action based on topTabId and creditPullInfo
 * @param {Object} creditPullInfo -
 * @param {String} topTabId
 * @param {Object} localizedStringBundle
 * @returns {Object} it returns object of gaAction and text.
 */
export const getGaActionAndText = (
  creditPullInfo: CreditPullInfo,
  topTabId: string,
  localizedStringBundle: StringMap
) => {
  if (topTabId === TU_PARTNER_CODE || topTabId === EXP_PARTNER_CODE) {
    return {text: localizedStringBundle['3B_REPORT_MODAL_TEXT'], gaAction: 'New 3B Report'};
  }
  if (creditPullInfo?.isPull1BScoreAllowed) {
    return {text: localizedStringBundle['1B_SCORE_MODAL_TEXT'], gaAction: 'New 1B Score'};
  }
  return {text: localizedStringBundle['1B_REPORT_MODAL_TEXT'], gaAction: 'New 1B Report'};
};

/**
 * Returns label and action type to trigger Ga event.
 * @param {Object} creditPullInfo -
 * @param {String} topTabId
 * @param {Object} localizedStringBundle
 * @returns {Array} it returns label and action for Ga event.
 */
export const getParamsForGaEvent = (creditPullInfo: $TSFixMe, topTabId: string, localizedStringBundle: StringMap) => {
  const label = topTabId === EFX_PARTNER_CODE ? `${topTabId} Get latest score & report` : `${topTabId} Confirm report`;
  const action =
    topTabId === EFX_PARTNER_CODE
      ? 'click'
      : getGaActionAndText(creditPullInfo, topTabId, localizedStringBundle).gaAction;
  return [label, action];
};

/**
 * Returns frequencyText according to frequencyUnitType
 * @param {string} frequencyType - credit frequency type {Days, Months, Year}
 * @param {Object} localizedStringBundle - string bundle according to country code
 * @returns {string} it returns frequency string from localizedStringBundle
 */
export const getFrequencyText = (frequencyType: string, localizedStringBundle: StringMap) => {
  switch (frequencyType) {
    case 'Days':
      return localizedStringBundle.DAILY;
    case 'Months':
      return localizedStringBundle.MONTHLY;
    case 'Years':
      return localizedStringBundle.ANNUAL;
    default:
      return '';
  }
};

/**
 * Returns frequencyText according to frequencyUnitType
 * @param {string} frequencyText - credit frequency text {daily, monthly, annual}
 * @param {Object} localizedStringBundle - string bundle according to country code
 * @param {boolean} isTextForToolTip - boolean var to differentiate whether the text is for modal or tooltip
 * @returns {string} it returns feature detail from localizedStringBundle
 */
export const getFeatureDetail = (
  frequencyText: string,
  localizedStringBundle: StringMap,
  isTextForToolTip: boolean
) => {
  let featureDetailStr = '';

  if (isTextForToolTip) {
    featureDetailStr =
      frequencyText === 'annual'
        ? interpolate(localizedStringBundle.TU_CREDIT_TEXT2, {frequencyText})
        : interpolate(localizedStringBundle.TU_CREDIT_TEXT1, {frequencyText});
  } else {
    featureDetailStr =
      frequencyText === 'annual'
        ? interpolate(localizedStringBundle.TU_RETRIEVE_REPORT_TEXT2, {frequencyText})
        : interpolate(localizedStringBundle.TU_RETRIEVE_REPORT_TEXT1, {frequencyText});
  }
  return featureDetailStr;
};

/**
 * Returns the score type based on the primary bureau and whether the user is a 3B user.
 * @param primaryBureau
 * @param is3BUser
 */
export const getScoreType = (primaryBureau = 'EFX', is3BUser = false) => {
  if (primaryBureau === 'TU') {
    return 'CA_1B';
  }
  return is3BUser ? 'US_3B' : 'US_EFX';
};

/**
 * Returns the report type based on the primary bureau and whether the user is a 3B user.
 * @param primaryBureau
 * @param is3BUser
 */
export const getReportType = (primaryBureau = 'EFX', is3BUser = false) => {
  return getScoreType(primaryBureau, is3BUser);
};
/**
 * Returns the provider based on the bureau
 * @param bureau
 * @returns provider
 */
export const getProvider = (bureau = 'EFX') => {
  return bureau === 'TU' ? 'CATU' : 'EFX';
};
